import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { QueryKeys } from "@/queries/utils";
import { CandidateStatus, CompanyJobOpening, NewOffer } from "@/types";

interface CounterOfferParams {
  openingId: string;
  offerId: string;
  offer: NewOffer;
}

export const useMakeCounterOffer = (
  onSuccess?: (data: CandidateStatus) => void,
) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({ offerId, offer }: CounterOfferParams) => {
      const token = await getAccessTokenSilently();

      return request<CandidateStatus>({
        method: "POST",
        path: `/api/jobOpening/offer/${offerId}/counter`,
        body: JSON.stringify(offer),
        token,
      });
    },
    onSuccess(data, variables) {
      onSuccess?.(data);

      toast.success("Your counter offer has been sent!");

      qc.setQueryData<CompanyJobOpening>(
        QueryKeys.companyOpportunity(variables.openingId),
        (old) => {
          if (!old) {
            return old;
          }

          const candidates = old.candidates ? [...old.candidates] : [];

          const candidateIdx = candidates.findIndex(
            (candidate) => candidate.id === data.id,
          );

          if (candidateIdx === -1) {
            return old;
          }

          candidates[candidateIdx] = data;

          return {
            ...old,
            candidates,
          };
        },
      );

      qc.setQueryData<CompanyJobOpening[]>(
        QueryKeys.companyOpportunities,
        (old) => {
          if (!old) {
            return old;
          }

          const opportunities = [...old];

          const opportunity = opportunities.find(
            (opportunity) => opportunity.id === variables.openingId,
          );

          if (!opportunity) {
            return old;
          }

          const candidates = opportunity.candidates
            ? [...opportunity.candidates]
            : [];

          const candidateIdx = candidates.findIndex(
            (candidate) => candidate.id === data.id,
          );

          if (candidateIdx === -1) {
            return old;
          }

          candidates[candidateIdx] = data;
          opportunity.candidates = candidates;

          return opportunities;
        },
      );

      void qc.invalidateQueries({
        queryKey: QueryKeys.candidateOpportunities,
      });
    },
  });

  return {
    makeCounterOffer: mutate,
    isMakingCounterOffer: isPending,
    counterOfferError: error,
  };
};
